// Lightweight skeletons that roughly mirror the real layout so data can pop in
// without jerks or big layout shifts. Neutral gray pulse tuned for the customer app.

const Pulse = ({ className = '' }) => (
    <div className={`animate-pulse bg-gray-200 rounded-md ${className}`} />
);

// One dish card: 4:3 image + title + action button (matches HomePage/BuilderPage)
export const DishCardSkeleton = () => (
    <div>
        <Pulse className="w-full aspect-[4/3] rounded-2xl" />
        <div className="pb-3 pt-[11px]">
            <Pulse className="h-4 w-4/5 rounded" />
            <Pulse className="h-4 w-2/5 rounded mt-2" />
            <Pulse className="h-9 w-full rounded-full mt-[10px]" />
        </div>
    </div>
);

export const DishGridSkeleton = ({ count = 6 }) => (
    <div className="grid grid-cols-2 gap-3" data-testid="dishes-grid-skeleton">
        {Array.from({ length: count }).map((_, i) => <DishCardSkeleton key={i} />)}
    </div>
);

// Sticky L1 category rail (circles + labels)
export const CategoryRailSkeleton = ({ count = 5 }) => (
    <div className="flex gap-1 overflow-hidden px-4 pt-3 pb-1 border-b border-[#E8E5DF]" data-testid="category-rail-skeleton">
        {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5 flex-shrink-0 w-[72px] px-1 pt-2 pb-1">
                <Pulse className="w-16 h-16 rounded-md" />
                <Pulse className="h-3 w-12 rounded" />
            </div>
        ))}
    </div>
);

export const FilterPillsSkeleton = ({ count = 4 }) => (
    <div className="flex gap-2 overflow-hidden px-4 pt-3 pb-2" data-testid="filter-pills-skeleton">
        {Array.from({ length: count }).map((_, i) => (
            <Pulse key={i} className="h-6 w-20 rounded-full flex-shrink-0" />
        ))}
    </div>
);

// Builder left rail (sub-collections)
export const BuilderRailSkeleton = ({ count = 5 }) => (
    <div className="w-[92px] flex-shrink-0 bg-white border-r border-gray-100" data-testid="builder-rail-skeleton">
        {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="w-full flex flex-col items-center gap-1.5 py-3 px-1">
                <Pulse className="w-12 h-12 rounded-full" />
                <Pulse className="h-3 w-12 rounded" />
            </div>
        ))}
    </div>
);

// Checkout time-slots grid (4 columns of pills)
export const TimeSlotsSkeleton = ({ count = 12 }) => (
    <div className="grid grid-cols-4 gap-2" data-testid="time-slots-skeleton">
        {Array.from({ length: count }).map((_, i) => (
            <Pulse key={i} className="h-11 rounded-full" />
        ))}
    </div>
);

// Full-page Home skeleton — used as the app-shell fallback (auth init + lazy chunk
// download) so the very first paint already matches the real layout, no spinner.
export const HomePageSkeleton = () => (
    <div className="min-h-screen bg-gray-50" data-testid="home-page-skeleton">
        <div className="max-w-[480px] mx-auto bg-gray-50 min-h-screen">
            {/* Dark-green hero: address + search/veg overlaid, full-bleed banner area below */}
            <div className="bg-[#277b3f]">
                <div className="px-4 pt-4 pb-3">
                    <div className="flex items-center justify-between">
                        <div className="min-w-0">
                            <div className="animate-pulse bg-white/20 h-3 w-16 rounded mb-2" />
                            <div className="animate-pulse bg-white/20 h-4 w-32 rounded" />
                        </div>
                        <div className="animate-pulse bg-white/30 w-11 h-11 rounded-full flex-shrink-0" />
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                        <div className="animate-pulse bg-white/80 flex-1 h-11 rounded-2xl" />
                        <div className="animate-pulse bg-white/30 w-11 h-6 rounded-full flex-shrink-0" />
                    </div>
                </div>
                {/* Full-bleed banner placeholder */}
                <div className="animate-pulse bg-white/10 h-[200px]" />
            </div>
            {/* Multiple-bookings banner placeholder */}
            <div className="px-4 pt-1">
                <div className="animate-pulse bg-black/10 w-full h-24 rounded-2xl" />
            </div>
            {/* Builder cards (gradient, name-top / image-bottom) */}
            <div className="grid grid-cols-3 gap-3 px-4 pt-3">
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="bg-gradient-to-b from-[#F5FF91] to-[#FCFFDC] rounded-2xl px-2 pt-2 pb-0 border border-[#F3F6CF] flex flex-col overflow-hidden">
                        <div className="animate-pulse bg-black/10 h-3 w-4/5 rounded mb-2 mx-auto" />
                        <div className="animate-pulse bg-black/10 w-[70%] aspect-[6/4] mx-auto mt-auto" />
                    </div>
                ))}
            </div>
            <CategoryRailSkeleton />
            <FilterPillsSkeleton />
            <div className="px-4 pt-2">
                <DishGridSkeleton count={6} />
            </div>
        </div>
    </div>
);

// Booking detail page skeleton — mirrors header + the stacked cards
export const BookingDetailSkeleton = () => (
    <div className="min-h-screen bg-gray-50 pb-10" data-testid="booking-detail-skeleton">
        {/* Header */}
        <div className="bg-white border-b border-gray-100 px-4 pt-5 pb-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
                <Pulse className="w-10 h-10 rounded-full" />
                <Pulse className="h-5 w-32 rounded" />
            </div>
            <Pulse className="h-10 w-24 rounded-full" />
        </div>
        <div className="px-4 pt-4 space-y-5">
            {/* Card 1: status + cook */}
            <div className="bg-white rounded-[8px] p-4 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                    <Pulse className="h-4 w-32 rounded" />
                    <Pulse className="h-6 w-20 rounded-full" />
                </div>
                <div className="bg-gray-50 rounded-[8px] p-4 border border-gray-100">
                    <Pulse className="h-3 w-24 rounded mb-3" />
                    <div className="flex items-center gap-3">
                        <Pulse className="w-11 h-11 rounded-full" />
                        <Pulse className="h-4 w-28 rounded" />
                    </div>
                </div>
            </div>
            {/* Card 2: order summary */}
            <div className="bg-white rounded-[8px] p-4 border border-gray-100">
                <Pulse className="h-5 w-36 rounded mb-3" />
                <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="flex items-center justify-between">
                            <Pulse className="h-4 w-40 rounded" />
                            <Pulse className="h-4 w-12 rounded" />
                        </div>
                    ))}
                </div>
                <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-100">
                    <Pulse className="h-4 w-24 rounded" />
                    <Pulse className="h-4 w-16 rounded" />
                </div>
            </div>
            {/* Card 3: details */}
            <div className="bg-white rounded-[8px] p-4 border border-gray-100 space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex items-start gap-3">
                        <Pulse className="w-5 h-5 rounded" />
                        <div className="flex-1">
                            <Pulse className="h-3 w-20 rounded mb-2" />
                            <Pulse className="h-4 w-40 rounded" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

// ===== Full-page, per-route app-shell skeletons =====

// Auth page: lime top with logo + feature row, white rounded card with input + button
export const AuthPageSkeleton = () => (
    <div className="min-h-screen flex flex-col" data-testid="auth-page-skeleton">
        <div className="flex-1 bg-[#d9ec00] flex flex-col items-center justify-center px-6 pt-12 pb-8">
            <div className="animate-pulse bg-black/10 h-16 w-40 rounded-2xl mb-4" />
            <div className="animate-pulse bg-black/10 h-5 w-48 rounded mb-8" />
            <div className="flex items-center justify-center gap-8">
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex flex-col items-center gap-2">
                        <div className="animate-pulse bg-black/10 w-8 h-8 rounded-full" />
                        <div className="animate-pulse bg-black/10 h-3 w-16 rounded" />
                    </div>
                ))}
            </div>
        </div>
        <div className="bg-white rounded-t-[2rem] -mt-6 px-6 py-8 shadow-lg space-y-4">
            <Pulse className="h-14 w-full rounded-full" />
            <Pulse className="h-14 w-full rounded-full" />
            <Pulse className="h-3 w-3/4 rounded mx-auto" />
        </div>
    </div>
);

// Cart page: white header + dishes card + schedule card + summary + bottom button
export const CartPageSkeleton = () => (
    <div className="min-h-screen bg-white pb-44" data-testid="cart-page-skeleton">
        <div className="bg-white border-b border-gray-100 px-4 py-4 flex items-center gap-3">
            <Pulse className="w-9 h-9 rounded-full" />
            <div>
                <Pulse className="h-3 w-16 rounded mb-2" />
                <Pulse className="h-4 w-40 rounded" />
            </div>
        </div>
        <div className="px-4 pt-4 space-y-5">
            <div className="bg-white rounded-[8px] border border-gray-200 p-4">
                <Pulse className="h-6 w-28 rounded mb-4" />
                {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="flex items-start gap-3 py-3 border-t border-gray-100 first:border-t-0">
                        <Pulse className="w-10 h-10 rounded-full flex-shrink-0" />
                        <div className="flex-1">
                            <Pulse className="h-4 w-32 rounded mb-2" />
                            <Pulse className="h-6 w-24 rounded-full" />
                        </div>
                        <Pulse className="h-5 w-12 rounded" />
                    </div>
                ))}
            </div>
            <div className="bg-white rounded-[8px] p-4 border border-gray-200">
                <Pulse className="h-6 w-40 rounded mb-4" />
                <div className="flex gap-2 mb-4">
                    {Array.from({ length: 4 }).map((_, i) => <Pulse key={i} className="h-10 w-20 rounded-full" />)}
                </div>
                <TimeSlotsSkeleton count={12} />
            </div>
            <div className="bg-[#F5F5F5] rounded-[8px] p-4 space-y-3">
                <div className="flex justify-between"><Pulse className="h-4 w-20 rounded" /><Pulse className="h-4 w-12 rounded" /></div>
                <div className="flex justify-between pt-2 border-t border-gray-300"><Pulse className="h-5 w-16 rounded" /><Pulse className="h-6 w-16 rounded" /></div>
            </div>
        </div>
    </div>
);

// /app download landing: lime hero (wordmark, tagline, image) + feature cards + badges
export const DownloadAppSkeleton = () => (
    <div
        className="min-h-screen w-full flex items-center justify-center px-6 py-12 bg-[#FBFCF3]"
        style={{ backgroundImage: 'radial-gradient(120% 60% at 50% 0%, #EAF77E 0%, rgba(234,247,126,0) 55%)' }}
        data-testid="download-app-skeleton"
    >
        <div className="w-full max-w-[400px] flex flex-col items-center">
            <Pulse className="h-20 w-40 rounded-2xl" />
            <Pulse className="mt-3 h-5 w-48 rounded" />
            <Pulse className="mt-4 h-3 w-64 rounded" />
            <Pulse className="mt-2 h-3 w-52 rounded" />
            <Pulse className="mt-10 h-3 w-28 rounded" />
            <div className="mt-4 flex flex-col items-center gap-3">
                <Pulse className="w-[180px] h-[52px] rounded-xl" />
                <Pulse className="w-[180px] h-[52px] rounded-xl" />
            </div>
            <Pulse className="mt-8 h-4 w-36 rounded" />
        </div>
    </div>
);

// Bookings list: white header + stacked booking cards
export const BookingsPageSkeleton = () => (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="bookings-page-skeleton">
        <div className="bg-white border-b border-gray-100 px-4 pt-5 pb-5 flex items-center justify-between">
            <Pulse className="h-5 w-36 rounded" />
            <Pulse className="h-10 w-24 rounded-full" />
        </div>
        <div className="px-4 pt-6 space-y-[13px]">
            {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white rounded-lg px-5 py-5 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                        <Pulse className="h-4 w-32 rounded" />
                        <Pulse className="h-6 w-20 rounded-full" />
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                        <Pulse className="h-4 w-3/4 rounded" />
                        <Pulse className="h-4 w-1/2 rounded" />
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                        <Pulse className="h-4 w-40 rounded" />
                        <Pulse className="h-5 w-14 rounded" />
                    </div>
                </div>
            ))}
        </div>
    </div>
);

// Profile: green header (avatar + stats) + white info/FAQ cards
export const ProfilePageSkeleton = () => (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="profile-page-skeleton">
        <div className="bg-[#006f3c] px-4 py-8">
            <div className="flex items-center gap-4">
                <div className="animate-pulse bg-white/25 w-16 h-16 rounded-full flex-shrink-0" />
                <div className="flex-1">
                    <div className="animate-pulse bg-white/25 h-5 w-32 rounded mb-2" />
                    <div className="animate-pulse bg-white/20 h-3 w-24 rounded" />
                </div>
                <div className="animate-pulse bg-white/20 h-9 w-16 rounded-full" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
                <div className="animate-pulse bg-white/10 h-16 rounded-xl" />
                <div className="animate-pulse bg-white/10 h-16 rounded-xl" />
            </div>
        </div>
        <div className="px-4 pt-4 space-y-4">
            <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
                {Array.from({ length: 3 }).map((_, i) => <Pulse key={i} className="h-14 w-full rounded-xl" />)}
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
                <Pulse className="h-4 w-32 rounded mb-3" />
                <Pulse className="h-3 w-full rounded" />
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm space-y-2">
                {Array.from({ length: 4 }).map((_, i) => <Pulse key={i} className="h-11 w-full rounded-xl" />)}
            </div>
        </div>
    </div>
);

// Builder page: header (back + search) + left rail + filter pills + dish grid
export const BuilderPageSkeleton = () => (
    <div className="min-h-screen bg-gray-50" data-testid="builder-page-skeleton">
        <div className="max-w-[480px] mx-auto bg-gray-50 min-h-screen flex flex-col">
            <div className="bg-white border-b border-gray-100 px-4 pt-4 pb-4">
                <div className="flex items-center gap-3">
                    <Pulse className="w-9 h-9 rounded-full" />
                    <Pulse className="h-5 w-40 rounded" />
                </div>
                <Pulse className="mt-3 h-11 w-full rounded-full" />
            </div>
            <div className="flex-1 flex min-h-0">
                <BuilderRailSkeleton />
                <div className="flex-1 min-w-0">
                    <FilterPillsSkeleton />
                    <div className="px-3 pt-2"><DishGridSkeleton count={6} /></div>
                </div>
            </div>
        </div>
    </div>
);


// Payment status (return from Cashfree): neutral gray bg + a centered white card
// mirroring the "verifying" state — NO lime homepage flash.
export const PaymentStatusSkeleton = () => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="payment-status-skeleton">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg flex flex-col items-center">
            <Pulse className="w-16 h-16 rounded-full mb-4" />
            <Pulse className="h-5 w-40 rounded mb-2" />
            <Pulse className="h-4 w-56 rounded" />
        </div>
    </div>
);
